import { LanderCookie } from "../commons/Constants";
import { getCookie } from "../commons/HelperFunctions";
import Logger from "../commons/Logger";

const ROLLER_ADS_CONFIG = {
  zeroClick: {
    endpoint: "https://309.eu.roller.ad/get-direct-click/godaddy/any",
    pid: "309",
    pk: "afa69c866-75ac4fb7f8ecd",
  },
  keywordClick: {
    endpoint: "https://343.eu.roller.ad/get-direct-click/godaddy1click/any",
    pid: "343",
    pk: "beeeb4834-ef89857d340e8",
  },
  // Second 1-click account, reached only through FeedType.ROLLER_ADS_1CLICK_387.
  keywordClick387: {
    endpoint: "https://387.eu.roller.ad/get-direct-click/godaddynew/any",
    pid: "387",
    pk: "7e85ccdc7-c2642e313f9a2",
  },
};

export interface RollerAd {
  url: string;
  bid: number;
  campaign_id?: number;
}

export interface RollerAdsResponse {
  ads: RollerAd[];
}

export interface RollerAdsFetchResult {
  status: number;
  ads: RollerAd[];
  error?: string;
}

interface RollerAdsFetchParams {
  rootDomain: string;
  customerId?: string;
  keywords?: string[];
  /** Use the keyword click endpoint (different RollerAds campaign with its own path/pid/pk). Defaults to 0-click auto-redirect endpoint. */
  keywordClick?: boolean;
  /** With keywordClick, use the second 1-click account (pid 387) instead of 343. */
  keywordClickAccount387?: boolean;
}

type RollerAdsAccountParams = Pick<
  RollerAdsFetchParams,
  "keywordClick" | "keywordClickAccount387"
>;

function getConfig(params: RollerAdsAccountParams) {
  if (params.keywordClick && params.keywordClickAccount387) {
    return ROLLER_ADS_CONFIG.keywordClick387;
  }
  if (params.keywordClick) return ROLLER_ADS_CONFIG.keywordClick;
  return ROLLER_ADS_CONFIG.zeroClick;
}

/** The RollerAds account (pid) a request with these params goes to. */
export function getRollerAdsPid(params: RollerAdsAccountParams): string {
  return getConfig(params).pid;
}

export async function fetchRollerAds(
  params: RollerAdsFetchParams
): Promise<RollerAdsFetchResult> {
  const ip =
    getCookie("caf_ipaddr_override") || getCookie(LanderCookie.CLIENT_IP);
  if (!ip) {
    return { status: 0, ads: [], error: "No client IP available" };
  }

  const config = getConfig(params);

  const searchParams = new URLSearchParams({
    pid: config.pid,
    pk: config.pk,
    ua: navigator.userAgent,
    ip,
    lang: navigator.language || "en",
    subid1: params.rootDomain,
    ...(params.customerId && { subid2: params.customerId }),
  });

  if (params.keywords?.length) {
    searchParams.set("keywords", params.keywords.join(","));
  }

  if (document.referrer) {
    searchParams.set("ref", document.referrer);
  }

  const url = `${config.endpoint}?${searchParams.toString()}`;
  Logger.debug("RollerAds fetch", {
    endpoint: config.endpoint,
    rootDomain: params.rootDomain,
    hasKeywords: Boolean(params.keywords?.length),
    keywordClick: Boolean(params.keywordClick),
    pid: config.pid,
  });

  try {
    const response = await fetch(url, {
      credentials: "omit",
    });

    if (response.status === 204) {
      return { status: 204, ads: [] };
    }

    if (!response.ok) {
      return {
        status: response.status,
        ads: [],
        error: `HTTP ${response.status}`,
      };
    }

    const data: RollerAdsResponse = await response.json();
    return { status: 200, ads: data.ads || [] };
  } catch (error) {
    Logger.error("RollerAds fetch error:", error);
    return { status: 0, ads: [], error: String(error) };
  }
}
